import pino from "pino";

// Get log level from environment variable, default to 'debug'
const getLogLevel = (): string => {
  // Try to get from environment variables
  // Note: import.meta.env.LOG_LEVEL will be replaced at build time by Vite
  const envLevel =
    import.meta.env.LOG_LEVEL ||
    (typeof process !== "undefined"
      ? (process as any)?.env?.LOG_LEVEL
      : undefined) ||
    "warn";

  // Validate log level
  const validLevels = ["trace", "debug", "info", "warn", "error", "fatal"];
  const level = envLevel.toLowerCase();

  return validLevels.includes(level) ? level : "warn";
};

// Create a browser-compatible logger
export const logger = pino({
  level: getLogLevel(),
  browser: {
    asObject: true,
    formatters: {
      level: (label) => ({ level: label }),
      log: (object) => object,
    },
    serialize: ["err", "error", "req", "res"],
    write: (object: any) => {
      const { time, level, msg, ...rest } = object;
      const now = new Date(time);
      const formattedDate = formatDate(now);

      // Use appropriate console method based on level
      const levelMethod = getLevelMethod(level);

      if (Object.keys(rest).length > 0) {
        console[levelMethod](`ascript | [${formattedDate}] ${msg}`, rest);
      } else {
        console[levelMethod](`ascript | [${formattedDate}] ${msg}`);
      }
    },
  },
});

const formatDate = (now: Date): string =>
  `${now.getDate().toString().padStart(2, "0")}/${(now.getMonth() + 1)
    .toString()
    .padStart(2, "0")}/${now.getFullYear().toString().slice(-2)} ${now
    .getHours()
    .toString()
    .padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}:${now
    .getSeconds()
    .toString()
    .padStart(2, "0")}:${now.getMilliseconds().toString().padStart(3, "0")}`;

const getLevelMethod = (level: number): "log" | "warn" | "error" | "debug" => {
  switch (level) {
    case 10: // trace
    case 20: // debug
      return "debug";
    case 30: // info
      return "log";
    case 40: // warn
      return "warn";
    case 50: // error
    case 60: // fatal
      return "error";
    default:
      return "log";
  }
};
